import DocumentModel = NetHealth.EHR.Domain.Models.Documents.DocumentModel;
import { EventApi } from '@fullcalendar/core';
import UserModel = NetHealth.EHR.Domain.Models.User.UserModel;
import ComponentOfCareInfoBasicModel = NetHealth.EHR.Domain.Models.ComponentOfCareInfoBasicModel;
import UserClinicianModel = NetHealth.EHR.Domain.Models.User.UserClinicianModel;
import FacilityFinalizedVisitRolesModel = NetHealth.EHR.Domain.Models.FacilitySettings.FacilityFinalizedVisitRolesModel;
import WorkflowStep from "Models/Workflow/WorkflowStep";
import DropdownOptionModel from "Models/DropdownOptionModel";
import { Module } from "vuex";
import { PatientInsuranceAuditReportModel } from "Models/AuditLog/PatientInsuranceAuditReportModel";
import { ClaimHoldAuditReportModel } from "Models/AuditLog/ClaimHoldAuditReportModel";
import BillingReviewAddressService from "Services/Billing/BillingReviewAddressService";
import PatientService from "Services/PatientService";
import AddressService from "Services/AddressService";
import IClinicians from "../Models/Clinician/Clinicians";
import { ClaimFilterModel, ClaimOverviewDataInput } from 'Models/Billing/ClaimsDashboard/ClaimFilterModel';
import ClaimSummaryResponseModel = NetHealth.EHR.Domain.Models.ClaimsDashboard.ClaimSummaryResponseModel;
import { SchedulerResourceView } from './Modules/Scheduler';
import VisitCountModel = NetHealth.EHR.Domain.Models.Visit.VisitCountModel;
import DocumentAuthorshipData = NetHealth.EHR.Domain.Models.Documents.DocumentAuthorshipData.DocumentAuthorshipDataModel
import { GetClaimDetailsForPatientDto, selectClaimStore, enterDOSStore,oldestToNewestStore } from 'Models/Billing/PatientPayments/PatientPaymentsModels';
import { Filter } from 'Services/PreferencesService';
import FacilityVisitTypeModel = NetHealth.EHR.Domain.Models.Visit.FacilityVisitTypeModel;
import VisitClassification = NetHealth.EHR.Domain.Models.Visit.VisitClassification;
import VisitEventModel = NetHealth.EHR.Domain.Models.Visit.VisitEventModel;
import SchedulerReservedTimeModel = NetHealth.EHR.Domain.Models.Scheduler.SchedulerReservedTimeModel;
import DischargeModel = NetHealth.EHR.Domain.Models.BatchDischarge.DischargeModel;

export interface FacilityState {
    encFacilityId: string;
    publicFacilityID: string;
    facilityAlias: string;
    twentyFourHourTime: boolean;
    timeZone: string;
    parentFacilityId: string;
    billingFacilityGroupId: number;
    finalizedVisitRoles: FacilityFinalizedVisitRolesModel;
}

export interface InsuranceState {
    selectedInsurance: {} | NetHealth.EHR.Domain.Models.Insurance.PatientInsuranceModel;
    isActiveStatusModalActive: boolean;
    isRemovePayerModalActive: boolean;
    dataLoading: boolean;
    loadingMessage?: string;
    visitLabel?: string;
    authEditActive?: boolean;
    facilityRemainingAuths?: number;
    currentTab?: number;
    treatmentCycleOptions?: NetHealth.EHR.Domain.Models.TreatmentCycle.TreatmentCycleDropDownListModel[];
    visits?: NetHealth.EHR.Domain.Models.Insurance.VisitInsuranceAuthorizationModel[];
}

export interface SchedulerState {
    visits: ReadonlyArray<VisitEventModel>;
    visitsLoading: boolean;
    reservedTimes: ReadonlyArray<SchedulerReservedTimeModel>;
    reservedTimesLoading: boolean;
    visitInfoConfig: ReadonlyArray<ComponentOfCareVisitInfoItemsModel>;
    isSingleDayView: boolean;
    isListView: boolean;
    filtersCollapsed: boolean;
    filtersLoading: boolean;
    isPrinting: boolean;
    requestsLoading: boolean;
    openPopoverMenus: string[];
    filters: SchedulerFilterState;
    schedulerConfig: SchedulerConfigurationState;
    facilityOfficeHours: NetHealth.EHR.Domain.Models.Scheduler.OfficeHoursAdminModel[];
    facilityVisitStatuses: NetHealth.EHR.Domain.Models.Scheduler.SchedulerFacilityVisitStatus[];
    schedulerFacilitySettings: NetHealth.EHR.Domain.Models.Scheduler.SchedulerFacilitySettingsModel;
    colorConfig: NetHealth.EHR.Domain.Models.Scheduler.ColorConfig;
    clinicians: ReadonlyArray<Clinician>;
    clinicianDataLoading: boolean;
    copyPasteResource?: VisitEventModel | SchedulerReservedTimeModel;
    copyPasteMethod?: 'cut' | 'copy';
    providerSchedules: ReadonlyArray<NetHealth.EHR.Domain.Models.Scheduler.SchedulerProviderSchedule>;
    requestedAppointmentId?: number;
    requestedAppointment?: NetHealth.EHR.Domain.Models.PatientPortalAppointmentRequestModel;
    appointmentRequests: ReadonlyArray<NetHealth.EHR.Domain.Models.PatientPortalAppointmentRequestModel>;
    loadingAppointmentRequests: boolean;
    activeAppointmentPanels: number;
    pendingRequests: number;
    totalVisitCount: number;
    totalReservedTimeCount: number;
    userCalendarFilterOptions: ReadonlyArray<Filter>;
    defaultUserCalendarFilterOption: Filter;
    visitStatusSelect: VisitStatusSelectState;
    rooms: ReadonlyArray<DropdownOptionModel>;
    secondaryClinicians: ReadonlyArray<Clinician>;
    visitTypeCount: VisitCountModel;
    patientsWithIntake: string[];
    previousSavedFilter: SchedulerFilterState;
    calendarScroll?: CalendarScrollInfo
}

export interface CalendarScrollInfo {
    top: number;
    left: number;
    autoScroll: boolean;
    isAsync: boolean;
}

export interface VisitStatusSelectState {
    visible: boolean
    button?: HTMLElement;
    visit?: VisitEventModel;
}

// TODO: import this from Clinicians Service
export interface Clinician {
    appId: number;
    clinicianCareComponents: ReadonlyArray<number>;
    clinicianTypes: ReadonlyArray<number>;
    discipline?: string;
    externalId: string;
    facilityId: string;
    firstName: string;
    id: number
    lastName: string;
    middleName: string;
    title?: string;
    userId: string;
}

export interface SchedulerVisitApi extends EventApi {
    extendedProps: VisitEventModel;
}

export interface SchedulerReservedTimeApi extends EventApi {
    extendedProps: SchedulerReservedTimeModel;
}

// Shimming this because the package doesn't provide types. This isn't complete.
export interface ContrastChecker {
    isLevelAA: (colorA: string, colorB: string, fontSize: number) => boolean;
    isLevelAAA: (colorA: string, colorB: string, fontSize: number) => boolean;
    [prop: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export enum SchedulerColorConfigType {
    DoNotColor = 'DoNotColor',
    VisitType = 'VisitType',
    VisitStatus = 'VisitStatus',
    Provider = 'Provider',
}

export interface SchedulerListRow {
    clinicianName: string;
    endTime?: string;
    isReservedTime?: boolean;
    title: string;
    startTime: string;
    visitType: string; // TODO: better name for this
    id: string;
    patientId: string;
    disableVisitNavigation?: boolean;
    disablePatientNavigation?: boolean;
    treatmentCycleId?: string;
    fullVisit?: VisitEventModel;
    colors: {
        foreground?: string;
        background?: string;
    }
}

export interface SchedulerResource {
    id: string;
    name: string
}

export interface SchedulerFilterContents {
    selectedClinicians: string[];
    selectedComponentsOfCare: string[];
    selectedVisitTypes: string[];
    isSingleDayView: boolean;
    isListView: boolean;
    isFacilityFilter?: boolean;
    showCancelledVisits: boolean;
    showNonOfficeHours: boolean;
    showUnavailableProviders: boolean;
}

export interface SchedulerFilterState extends SchedulerFilterContents {
    id?: number;
    startDate: string;
    endDate: string;
    selectedSecondaryClinicians: string[];
    selectedRooms: string[];
    resourceView: SchedulerResourceView;
    loginId: string;
}

export interface SchedulerConfigurationState {
    id?: number;
    rowHeight: number;
    colorTileDefaultFont: string;
    colorTileDefaultBackground: string;
    listViewBlankSpace: boolean;
    listViewPrintColumns: string[];
    listViewColumns: string[];
    listSortColumn: string[];
    listSortDesc: boolean[];
    groupListBy: string;
}

export interface WorklistState {
    patients: ReadonlyArray<NetHealth.EHR.Domain.Models.Clinician.PatientVisitSummaryModel>;
    patientCount: number;
    patientPagination: any;
    patientsLoading: boolean;
    clinicians: ReadonlyArray<any>; // TODO no any
    disciplines: string[];
    units: string[];
    filters: {
        patientNameSearch: string;
        selectedComponentsOfCare: string[];
        selectedClinicians: string[];
        selectedUnits: string[];
        selectedDisciplines: string[];
        showInactivePatients: boolean;
        onHoldFilterValue: boolean;
        showStalledWound: boolean;
    };
    filtersLoading: boolean;
    selectedWorklist: string;
    treatmentCycleVisits: ReadonlyArray<NetHealth.EHR.Domain.Models.Visit.PatientTreatmentCycleVisits>;
}

export interface WorkflowState {
    visit: NetHealth.EHR.Domain.Models.Visit.VisitModel;
    currentWorkflow: NetHealth.EHR.Domain.Models.WorkflowAdmin.WorkflowModel;
    currentWorkflowGroups: NetHealth.EHR.Domain.Models.Workflow.WorkflowGroupModel[];
    defaultWorkflowGroups: NetHealth.EHR.Domain.Models.Workflow.WorkflowGroupModel[];
    workflows: NetHealth.EHR.Domain.Models.WorkflowAdmin.WorkflowModel[];
    alerts: NetHealth.EHR.Domain.Models.Alerts.ActionTestResultsModel[];
    notes: NetHealth.EHR.Domain.Models.Menu.ParentMenuItem[];
    visitActions: NetHealth.EHR.Domain.Models.Menu.ParentMenuItem[];
    currentWorkflowStep: WorkflowStep;
    previousWorkflowStep: WorkflowStep;
    workflowButtonsDisabled: boolean;
    userCanEdit: boolean;
    savePromise: Promise<any>;
    launchPage: string;
    activeTab:number;
    isSaveAndNextClicked: boolean;
}

export interface DocumentEditorConfig {
    component: () => Promise<any>;
    module: Module<any, {}>;
    featureToggle?: string;
    useDocsApi?: boolean;
    featureToggleDefault ?: boolean;
    isParentChildDoc?: boolean;
}

export const enum DocumentEditorType {
    Iframe = 0,
    Knockout = 1,
    Vue = 2,
}

export interface DocumentAlertResponse {
    action: string;
    reason: string;
}

export interface DocumentLibraryState {
    document: DocumentModel|NetHealth.MedicalDocuments.Models.DocumentModel.Document;
    parentDocument: DocumentModel;
    documents: DocumentModel[];
    parentDocuments: DocumentModel[];
    previousDocuments: NetHealth.EHR.Domain.Models.PreviousDocumentMetaModel[];
    previousDocumentViewer: string;
    initialDocumentHtmlModel: string;
    documentModelCheckDisabled: boolean;
    documentLibrarySettings: DocumentLibrarySettingsState;
    criticalFieldProperties: NetHealth.EHR.Domain.Models.CriticalFieldModel[];
    documentAlerts: NetHealth.EHR.Domain.Models.Alerts.ActionTestResultsModel[];
    documentAlertResponses: DocumentAlertResponse[];
    addendums: NetHealth.EHR.Domain.Models.AddendumModel[];
    customQuestions: NetHealth.EHR.Domain.Models.CustomQuestion[];
    documentEditorMode: DocumentEditorMode;
    documentEditorValidationMode: DocumentEditorValidationMode;
    isParentDocument: boolean;
    isDocumentLoaded: boolean;
    isSignatureValid: boolean;
    user: NetHealth.EHR.Domain.Models.User.UserCredentialsModel;
    isDraftDocument: boolean;
    vendorId: number;
    documentAuthorshipData: DocumentAuthorshipData;
    parentDocumentId: string;
    parentDocumentChanged: boolean;
    isParentDocumentsLoaded: boolean;
    invalidAlerts: boolean;
}

export interface DocumentLibrarySettingsState {
    addendumsEnabled: boolean;
    criticalFieldsEnabled: boolean;
    customQuestionsEnabled: boolean;
    documentPropertiesEnabled: boolean;
    notesEnabled: boolean;
    docsApiEnabled: boolean;
}

export const enum DocumentEditorMode {
    Edit = 0,
    CriticalFieldEdit = 1
}

export const enum DocumentEditorValidationMode {
    Normal = 0,
    Recommended = 1
}

export const enum DocumentType {
    MultiWoundChartNotes = "MultiWoundChartNotesDocument",
    PlanOfCare = "PlanOfCareDocument",
    WoundTreatmentNotes = "WoundTreatmentNotesDocument",
    WoundAssessment = "WoundAssessmentDocument",
    ProgressNote = "ProgressNoteDocument"
}

export interface PlanDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.PlanDocumentModel;
}

export interface PTOrthoEvaluationDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.PTOrthoEvaluationDocumentModel;
}

export interface OTOrthoEvaluationDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.OTOrthoEvaluationDocumentModel;
}

export interface PTNeuroEvaluationDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.PTNeuroEvaluationDocumentModel;
}

export interface OTNeuroEvaluationDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.OTNeuroEvaluationDocumentModel;
}

export interface SpeechLanguageEvaluationDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.SpeechLanguageEvaluationDocumentModel;
}

export interface CognitiveFunctioningDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.CognitiveFunctioningDocumentModel;
}

export interface FallRiskAssessmentDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.FallRiskAssessmentDocumentModel;
}

export interface PatientHistoryDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.PatientHistoryDocumentModel;
}

export interface ClinicalSwallowEvalDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.ClinicalSwallowEvalDocumentModel;
}

export interface PatientParkingLotState {
    entries: NetHealth.EHR.Domain.Models.PatientParkingLotEntryModel[];
}

export interface SLPPassyMuirEvaluationDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.SLPPassyMuirEvaluationDocumentModel;
}

export interface InstrumentSwallowEvalDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.InstrumentSwallowEvalDocumentModel;
}

export interface ProgressNoteDocumentEditorState {
     document: NetHealth.EHR.Domain.Models.Documents.ProgressNoteDocumentModel;
}

export interface PhysicianCommunicationDocumentEditorState {
     document: NetHealth.EHR.Domain.Models.Documents.PhysicianCommunicationDocumentModel;
}

export interface AllergyListDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.AllergyListDocumentModel;
}

export interface LymphedemaDocumentEditorState {
    documents: NetHealth.EHR.Domain.Models.Documents.LymphedemaDocumentModel[];
    addingNewLymphedema: boolean;
}

export interface LymphedemaTreatmentDocumentEditorState {
    document: NetHealth.EHR.Domain.Models.Documents.LymphedemaTreatmentDocumentModel;
}

export interface FinalizeWorkflowEditorState {
    visitId: string;
    patientId: string;
    allReports: DropdownOptionModel[];
    allClinicalReports: NetHealth.EHR.Domain.Models.FinalizeVisit.FinalizeVisitClinicalReportsModel;
    allDocuments: NetHealth.EHR.Domain.Models.FinalizeVisit.FinalizeVisitDocumentsModel;
    allWarnings: NetHealth.EHR.Domain.Models.FinalizeVisit.FinalizeVisitWarningsModel;
    allBillingInfo: NetHealth.EHR.Domain.Models.FinalizeVisit.FinalizeVisitBillingModel;
    allHistory: NetHealth.EHR.Domain.Models.FinalizeVisit.FinalizeVisitHistoryModel;
    selectedDocsReqSignatures: string[];
    selectedOtherDocuments: string[];
    documentSignatureListId: number;
    documentSignatureListValues: string[];
    requestFeedbackMessage: string;
    checkmarkForScheduleVisit: boolean;
    checkmarkForRequestFeedback: boolean;
    markVisitAsFinal: boolean;
    generateClaim: boolean;
    markReEvalAsComplete: boolean;
    generateProgressReport: boolean;
    generateReEvalReport: boolean;
    generatePlanOfCareReport: boolean;
    prepareFaxesForSending: boolean;
    addToExternalCommunicationTracking: boolean;
    dischargeTreatmentCycle: boolean;
    dischargeAdmission: boolean;
    dischargeTreatmentCycleDate: string;
    dischargeAdmissionDate: string;
    dischargeAdmissionStatus: string;
    signatureIsValid: boolean;
}

export const enum NotificationType {
    Success = 'notify-success',
    Warning = 'notify-warning',
    Error = 'notify-error'
}

export interface Notification {
    key?: string;
    notificationType: NotificationType;
    message: string;
    title?: string;
    extendedMessage?: string;
    persistent?: boolean;
    dismissText?: string;
    maxWidth?: number;
}

export interface ModalState {
    key: string;
    name: string;
    headerTitle?: string;
    keepOnNavigate?: boolean;
    disallowMultiple?: boolean;
    props: any;
    resolve: any;
}

export interface PharmacySearchState {
    stateOptions: DropdownOptionModel[];
    customPharmacy: NetHealth.EHR.Domain.Models.Pharmacy.PharmacyModel;
    searchResults: NetHealth.EHR.Domain.Models.Pharmacy.PharmacyModel[];
    searchValues: NetHealth.EHR.Domain.Models.Pharmacy.PharmacySearchModel;
}

export interface ConfirmationModalState {
    headerTitle?: string;
    message?: string;
    itemsMessage?: string;
    items?: string[];
    showCancel?: boolean;
    showOk?: boolean;
    keepOnNavigate?: boolean;
    showClose?: boolean;
    showYes?: boolean;
    showNo?: boolean;
    confirmAction?: any;
    declineAction?: any;
    photoGuid?: string;
}

export interface RootState {
    userId: string;
    username?: string;
    userEmail?: string;
    uniqueUserId?: string;
    userLoginId?: string;
    user?: UserModel;
    userRoles?: string[];
    userClinicians?: UserClinicianModel[];
    userComponentsOfCare?: ComponentOfCareInfoBasicModel[];
    facility?: FacilityState;
    facilitySettings?: any;
    featureToggles?: {
    };
    authorizations?: {
    };
    userSettings?: {
        // Add more as needed
        displayVerticalWorkflow: boolean;
        myReportsOpenInNewWindow: boolean;
    };
    modals?: ModalState[];
    notifications?: Notification[];

    // KO state
    koFacilitySettings?: any;

    visitTypeAutoAuthorizationSelectionConfig?: NetHealth.EHR.Domain.Models.Insurance.VisitTypeAuthorizationAutoSelectionConfig[];

    // module state (for typing in static RootStore references)
    workflow?: WorkflowState;
    documentLibrary?: DocumentLibraryState;
    csChat?: CsChatState;
    visitClassifications?: VisitClassification[];
    facilityVisitTypes?: FacilityVisitTypeModel[];
    nhMessagingAccessUrl?: string;
    nhMessagingApiUrl?: string;
    appImageConfig?: NetHealth.EHR.Domain.UiModels.AppImageConfigModel;
    numberOfTabsOpened?: BrowserTabCounter;
    taliSdkUrl?: string;
}

export interface BrowserTabCounter {
    totalTabs: number;
    documentationTabs: number;
}

export interface CsChatState {
    chatProvider?: string;
    widgetVisibility?: string;
    customerStatus?: string;
    haveUnreadAgentMessages?: any;
}

export interface ProgressNoteConfigState {
    tabIndex: number;
    templateName: string;
    progressNoteUserTemplateGroupID: string;
    subjectiveSection: string;
    objectiveSection: string;
    assessmentSection: string;
    planSection: string;
    proceduresSection: string;
    userGroups: DropdownOptionModel[];
    userGroupTemplates: NetHealth.EHR.Domain.Models.ProgressNoteUserKeywordTemplateModel[];
    documentSections: NetHealth.EHR.Domain.Models.ProgressNoteDocumentSections[];
    importableUsers: DropdownOptionModel[];
    userId: string;
    fromUserId: string;
    toUserId: string;
    copyUsersTemplate: DropdownOptionModel[];
    copyTemplateUserGroupID: string;
}

export interface BillingReviewAddressUpdateState {
    patients: any;
    document: any;
    hasPrimaryPhoneNumber: boolean;
    hasSecodaryPhoneNumber: boolean;
    addressTypeOptions: any;
    isAddressUpdated: boolean;
    isGuarantorAddressUpdated: boolean;
    selectedReviewAddress: any;
    hasPatientInfo: boolean;
    billingReviewAddressService: BillingReviewAddressService;
    patientService: PatientService;
    addressService: AddressService;
}

export interface PatientInsuranceAuditReportState {
    reportData: PatientInsuranceAuditReportModel[];
    initialReportData: PatientInsuranceAuditReportModel[];
    filters: {
        userNameSearch: string,
        toDate: Date,
        fromDate: Date
    };
    patientName: string;
    patientDob: Date;
    insuranceName: string;
    policySeq: string;
}


export interface ClaimHoldAuditReportState {
    reportData: ClaimHoldAuditReportModel[];
    initialReportData: ClaimHoldAuditReportModel[];
    filters: {
        userNameSearch: string,
    };
    patientName: string;
    patientDob: Date;
    claimId: number;
}

export interface ManagementBoardState {
    quickViewCollapsed: boolean;
    showHiddenProviders: boolean;
    hiddenProviderIds: string[];
    filters: {
        selectedComponentsOfCare: string[];
        pt: boolean;
        ot: boolean;
        slp: boolean;
    }
}

export interface VisitWorklistState {
    visits: ReadonlyArray<NetHealth.EHR.Domain.Models.Visit.VisitWorklistModel>;
    visitCount: number;
    visitPagination: any;
    visitsLoading: boolean;
    filtersLoading: boolean;
    clinicians: IClinicians[];
    filters: {
        patientNameSearch: string,
        selectedClinicians: string[];
        fromDate: string;
        toDate: string;
        today: boolean;
        selectedVisitStatuses: number[];
        showStalledWound: boolean;
    };
    facilityVisitStatuses: ReadonlyArray<NetHealth.EHR.Domain.Models.Scheduler.SchedulerFacilityVisitStatus>;
}

export interface VisitsAndDocumentationLandingPageState {
    patientId: string;
    searchModel: NetHealth.EHR.Domain.Models.PatientDashboard.VisitsAndDocumentationSearchModel;
    page: NetHealth.EHR.Domain.Models.PatientDashboard.LandingPages.VisitsAndDocumentationLandingPage;
}

export interface ClaimsDashboardUpdateState {
    filtersDisabled: boolean;
    selectedKPI: string;
    filtersCollapsed: boolean;
    filterRequestModel: ClaimFilterModel;
    claimList: ClaimSummaryResponseModel[];
    showClaimsDashboardLoader: boolean;
    graphsCollapsed: boolean;
    claimOverviewDataInput: ClaimOverviewDataInput;
    refreshClaimsDashboardData: boolean;
    componentsList: {
        id: number,
        componentName: string,
        component: any
    }[];
    isCustomFiltersClicked: boolean;
    currentPage: string;
    selectedClaimStatus: number;
    customSortColumn: number;
    isSortAscending: boolean;
}

export interface PatientPaymentUpdateState {
    saveTransaction:boolean,
    paymentPlan:any,
    paymentPlandata:any,
    patientPayment: {
        accountNumber: string,
        debtorId: number,
        facilityId: number,
        reference: string,
        transactionAmount: number,
        transactionDate: Date,
        transactionIds: number[],
        transactionDescriptionId: number,
        transactionPreclaimIds: number[],
        unApplied: number,
        unAppliedReasonID: number,
        dateOfServiceFrom: Date,
        claimIds: number[],
        claimDetailIds: number[],
        providerId: number
    },
    patientId: number;
    patientName: string;
    patientAddress1: string;
    patientAddress2: string;
    patientAddress3: string;
    isNavigatedFromTransactionPage: boolean;
    currentPostingAction: number;
    currentFindClaimValue: number;
    selectedDebtors: DropdownOptionModel[];
    currentDateOfServiceFilter: Date;
    dollarZeroFilter: boolean;
    selectedListItem: GetClaimDetailsForPatientDto[];
    currentSelectedFacility: number;
    currentTransactionId: number;
    selectedCurrentProvider: DropdownOptionModel;
    selectClaimStore: selectClaimStore;
    enterDOSStore: enterDOSStore;
    oldestToNewestStore: oldestToNewestStore;
    currentLastSelectedClaimList: GetClaimDetailsForPatientDto[];
    isNewPatientDataLoaded: boolean;
    isSaveClicked: boolean;
    isSaveAndPrintReceiptClicked: boolean;
    isPatientInformationLoad: boolean;
    batchId: number;
    enableFields: boolean;
    isNavigatedFromSchedulerPage: boolean;
    dateOfService: string;
    therapistId: string;
}

export interface PaymentBatchesState {
    filtersDisabled: boolean;
    filtersCollapsed: boolean;
    paymentBatches: [];
    createdFromDate: string;
    createdToDate: string;
    batchId: number;
    paymentAmount: string;
    createdById: number;
    batchProof: string;
    releasedFlag: number;
    dateRangeFlag: number;
    paymentCheckRef: string;
    billingPaymentBatchesModernization: boolean;
}

export interface BatchDischargeState {
    providers: IClinicians [];
    providerTypes: string [];
    dischargeItems: DischargeModel [];
    selectedItems: string [];
    selectedTable: string;
    isAdmissionsEnabled: boolean;
    pendingDischargeJobs: DischargeModel[];
    admissionPagination: any;
    treatmentCyclePagination: any;
    userIsPhysician: any;
    filters: {
        selectedComponentsOfCare: string [];
        daysSinceLastVisitFilter: number;
        selectedProviders: string [];
        selectedProviderTypes: string [];
        isPtDisciplineFilter: boolean;
        isOtDisciplineFilter: boolean;
        isSlpDisciplineFilter: boolean;
    }
}